import { useEffect, useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import {
  Alert,
  Badge,
  Box,
  Button,
  Collapse,
  Group,
  Modal,
  ScrollArea,
  Select,
  Stack,
  Text,
  Textarea,
  Title,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { useForm } from '@mantine/form';
import ReactDiffViewer, { DiffMethod } from 'react-diff-viewer-continued';
import GraphqlError from '@components/graphql-error';
import LoadingData from '@components/graphql-loading-data';
import {
  GET_PROMPTS,
  GET_PROMPT_VERSIONS,
  UPDATE_PROMPT,
  SET_ACTIVE_PROMPT_VERSION,
  DISCARD_PROMPT_DRAFT,
} from '@queries/prompts';
import type { GetPromptsQuery, GetPromptVersionsQuery } from '@src/__generated__/graphql';

type PromptSummary = GetPromptsQuery['getPrompts'][number];
type PromptVersionRow = GetPromptVersionsQuery['getPromptVersions'][number];

const ACTIVE_BG = 'var(--mantine-color-green-0)';
const ACTIVE_BORDER = 'var(--mantine-color-green-5)';

type VersionKind = 'active' | 'draft' | 'historical';

const versionKind = (v: PromptVersionRow): VersionKind => {
  if (v.isActive) return 'active';
  if (!v.everActive) return 'draft';
  return 'historical';
};

const findDraft = (versions: PromptVersionRow[]): PromptVersionRow | undefined =>
  versions.find((v) => versionKind(v) === 'draft');

const formatDate = (value?: string | null): string => {
  if (!value) return '-';
  const d = new Date(value);
  return isNaN(d.getTime()) ? value : d.toLocaleString();
};

function VersionDiff({ oldValue, newValue }: { oldValue: string; newValue: string }) {
  return (
    <Box mt="xs" style={{ border: '1px solid var(--mantine-color-gray-3)', borderRadius: 4, overflow: 'hidden' }}>
      <ReactDiffViewer
        oldValue={oldValue}
        newValue={newValue}
        splitView
        compareMethod={DiffMethod.TRIMMED_LINES}
        leftTitle="Previous"
        rightTitle="This version"
        styles={{
          contentText: {
            fontFamily:
              'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
            fontSize: 12,
            lineHeight: '1.5',
          },
        }}
      />
    </Box>
  );
}

function VersionRow({
  version,
  previous,
  onSetActive,
  onDiscard,
  activatingId,
  discardingId,
}: {
  version: PromptVersionRow;
  previous?: PromptVersionRow;
  onSetActive: (id: string) => void;
  onDiscard: (id: string) => void;
  activatingId: string | null;
  discardingId: string | null;
}) {
  const [diffOpened, { toggle: toggleDiff }] = useDisclosure(false);
  const hasPrevious = !!previous;
  const kind = versionKind(version);

  const badge =
    kind === 'active' ? (
      <Badge color="green" size="xs">
        active
      </Badge>
    ) : kind === 'draft' ? (
      <Badge color="yellow" size="xs">
        draft
      </Badge>
    ) : (
      <Badge color="gray" size="xs" variant="light">
        historical
      </Badge>
    );

  return (
    <Box
      p="sm"
      style={{
        border: `1px solid ${kind === 'active' ? ACTIVE_BORDER : 'var(--mantine-color-gray-3)'}`,
        borderRadius: 4,
        backgroundColor: kind === 'active' ? ACTIVE_BG : undefined,
      }}
    >
      <Group justify="space-between" mb={4}>
        <Group gap="xs">
          <Text size="sm" fw={500}>
            v{version.version}
          </Text>
          {badge}
        </Group>
        <Group gap="xs">
          <Text size="xs" c="dimmed">
            {formatDate(version.createdAt)}
          </Text>
          {hasPrevious && (
            <Button size="compact-xs" variant="subtle" onClick={toggleDiff}>
              {diffOpened ? 'Hide changes' : `Diff vs v${previous.version}`}
            </Button>
          )}
          {kind !== 'active' && (
            <Button
              size="compact-xs"
              color="green"
              variant="filled"
              loading={activatingId === version.id}
              onClick={() => onSetActive(version.id)}
            >
              Set Active
            </Button>
          )}
          {kind === 'draft' && (
            <Button
              size="compact-xs"
              color="red"
              variant="light"
              loading={discardingId === version.id}
              onClick={() => onDiscard(version.id)}
            >
              Discard
            </Button>
          )}
        </Group>
      </Group>

      {hasPrevious && diffOpened ? (
        <VersionDiff oldValue={previous.content} newValue={version.content} />
      ) : (
        <ScrollArea.Autosize mah={120}>
          <Text size="xs" style={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}>
            {version.content}
          </Text>
        </ScrollArea.Autosize>
      )}
    </Box>
  );
}

function CompareTwoPanel({ versions }: { versions: PromptVersionRow[] }) {
  const [opened, { toggle }] = useDisclosure(false);
  const newest = versions[0];
  const secondNewest = versions[1];
  const [baseId, setBaseId] = useState<string>(secondNewest.id);
  const [compareId, setCompareId] = useState<string>(newest.id);

  const options = versions.map((v) => {
    const kind = versionKind(v);
    return {
      value: v.id,
      label: `v${v.version}${kind === 'active' ? ' (active)' : kind === 'draft' ? ' (draft)' : ''} — ${formatDate(v.createdAt)}`,
    };
  });

  const base = versions.find((v) => v.id === baseId);
  const compare = versions.find((v) => v.id === compareId);
  const sameVersion = baseId === compareId;

  return (
    <Box p="sm" style={{ border: '1px dashed var(--mantine-color-gray-4)', borderRadius: 4 }}>
      <Group justify="space-between">
        <Text size="sm" fw={500}>
          Compare any two versions
        </Text>
        <Button size="compact-xs" variant="subtle" onClick={toggle}>
          {opened ? 'Hide' : 'Open'}
        </Button>
      </Group>
      {opened && (
        <>
          <Group grow mt="xs" align="flex-end">
            <Select
              label="Base"
              data={options}
              value={baseId}
              onChange={(v) => v && setBaseId(v)}
              allowDeselect={false}
            />
            <Select
              label="Compare"
              data={options}
              value={compareId}
              onChange={(v) => v && setCompareId(v)}
              allowDeselect={false}
            />
          </Group>
          {sameVersion ? (
            <Text size="xs" c="dimmed" mt="xs">
              Pick two different versions to see a diff.
            </Text>
          ) : base && compare ? (
            <VersionDiff oldValue={base.content} newValue={compare.content} />
          ) : null}
        </>
      )}
    </Box>
  );
}

function PromptRow({ prompt }: { prompt: PromptSummary }) {
  const [historyOpened, { toggle: toggleHistory, open: openHistory }] = useDisclosure(false);
  const [editOpened, { open: editOpen, close: editClose }] = useDisclosure(false);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [activatingId, setActivatingId] = useState<string | null>(null);
  const [discardingId, setDiscardingId] = useState<string | null>(null);

  const {
    loading: versionsLoading,
    error: versionsError,
    data: versionsData,
  } = useQuery(GET_PROMPT_VERSIONS, {
    variables: { promptKey: prompt.promptKey },
  });

  const versions: PromptVersionRow[] = [...(versionsData?.getPromptVersions ?? [])].sort(
    (a, b) => b.version - a.version
  );
  const draft = findDraft(versions);
  const nextDraftVersion = (versions[0]?.version ?? prompt.version) + (draft ? 0 : 1);

  const form = useForm({ initialValues: { content: draft?.content ?? prompt.content } });

  // Reset the editor's starting content when the draft changes (or appears/disappears).
  useEffect(() => {
    if (!editOpened) {
      form.setValues({ content: draft?.content ?? prompt.content });
      form.resetDirty({ content: draft?.content ?? prompt.content });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft?.id, draft?.content, prompt.content, editOpened]);

  const [updatePromptMutation, { loading: saving, error: saveError }] = useMutation(UPDATE_PROMPT, {
    refetchQueries: [
      { query: GET_PROMPTS },
      { query: GET_PROMPT_VERSIONS, variables: { promptKey: prompt.promptKey } },
    ],
    onCompleted: (data) => {
      setSuccessMsg(
        `Draft v${data.updatePrompt.version} saved. Use Version History to diff and Set Active when ready.`
      );
      editClose();
      openHistory();
    },
  });

  const [setActiveMutation] = useMutation(SET_ACTIVE_PROMPT_VERSION, {
    refetchQueries: [
      { query: GET_PROMPTS },
      { query: GET_PROMPT_VERSIONS, variables: { promptKey: prompt.promptKey } },
    ],
  });

  const [discardMutation] = useMutation(DISCARD_PROMPT_DRAFT, {
    refetchQueries: [
      { query: GET_PROMPTS },
      { query: GET_PROMPT_VERSIONS, variables: { promptKey: prompt.promptKey } },
    ],
  });

  const handleSave = form.onSubmit(async ({ content }) => {
    setSuccessMsg(null);
    await updatePromptMutation({ variables: { promptKey: prompt.promptKey, content } });
  });

  const handleSetActive = async (id: string) => {
    setActivatingId(id);
    try {
      await setActiveMutation({ variables: { id } });
    } finally {
      setActivatingId(null);
    }
  };

  const handleDiscard = async (id: string) => {
    setDiscardingId(id);
    try {
      await discardMutation({ variables: { id } });
    } finally {
      setDiscardingId(null);
    }
  };

  return (
    <Box
      p="md"
      style={{
        border: '1px solid var(--mantine-color-gray-3)',
        borderRadius: 6,
      }}
      mb="sm"
    >
      <Group justify="space-between" mb={4}>
        <Stack gap={2}>
          <Group gap="xs">
            <Title order={5}>{prompt.label}</Title>
            <Badge color="blue" size="xs" variant="light">
              {prompt.promptKey}
            </Badge>
            <Badge color="green" size="xs">
              active v{prompt.version}
            </Badge>
            {draft && (
              <Badge color="yellow" size="xs">
                draft v{draft.version}
              </Badge>
            )}
          </Group>
          {prompt.description && (
            <Text size="xs" c="dimmed">
              {prompt.description}
            </Text>
          )}
        </Stack>
        <Group gap="xs">
          {draft && (
            <>
              <Button
                size="xs"
                color="green"
                loading={activatingId === draft.id}
                onClick={() => handleSetActive(draft.id)}
              >
                Set Draft v{draft.version} Active
              </Button>
              <Button
                size="xs"
                color="red"
                variant="light"
                loading={discardingId === draft.id}
                onClick={() => handleDiscard(draft.id)}
              >
                Discard Draft
              </Button>
            </>
          )}
          <Button size="xs" variant="subtle" onClick={toggleHistory}>
            {historyOpened ? 'Hide History' : 'Version History'}
          </Button>
          <Button size="xs" onClick={editOpen}>
            {draft ? `Edit Draft v${draft.version}` : 'Edit'}
          </Button>
        </Group>
      </Group>

      {successMsg && (
        <Alert color="green" variant="light" mb="xs" withCloseButton onClose={() => setSuccessMsg(null)}>
          {successMsg}
        </Alert>
      )}

      <Collapse in={historyOpened}>
        {versionsLoading ? (
          <LoadingData />
        ) : versionsError ? (
          <GraphqlError error={versionsError} />
        ) : (
          <Stack gap="xs" mt="sm">
            {versions.length >= 2 && <CompareTwoPanel versions={versions} />}
            {versions.map((v, idx) => (
              <VersionRow
                key={v.id}
                version={v}
                previous={versions[idx + 1]}
                onSetActive={handleSetActive}
                onDiscard={handleDiscard}
                activatingId={activatingId}
                discardingId={discardingId}
              />
            ))}
          </Stack>
        )}
      </Collapse>

      <Modal
        opened={editOpened}
        onClose={editClose}
        title={draft ? `Edit Draft v${draft.version}: ${prompt.label}` : `Edit: ${prompt.label}`}
        fullScreen
        styles={{ close: { background: 'transparent', border: 'none', boxShadow: 'none' } }}
      >
        <form onSubmit={handleSave} style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 120px)' }}>
          <Textarea
            label="Prompt Content"
            description={
              draft
                ? `Save updates draft v${draft.version} in place. The live prompt does not change until you Set Active.`
                : `Save creates draft v${nextDraftVersion}. The live prompt (v${prompt.version}) does not change until you Set Active.`
            }
            styles={{
              root: { flex: 1, display: 'flex', flexDirection: 'column' },
              wrapper: { flex: 1, display: 'flex' },
              input: {
                flex: 1,
                fontFamily:
                  'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
                fontSize: 13,
                lineHeight: 1.5,
              },
            }}
            {...form.getInputProps('content')}
          />
          {saveError && (
            <Alert color="red" variant="light" mt="sm">
              {saveError.message}
            </Alert>
          )}
          <Group justify="flex-end" mt="md">
            <Button variant="subtle" onClick={editClose}>
              Cancel
            </Button>
            <Button type="submit" loading={saving}>
              {draft ? 'Save Draft' : 'Save as Draft'}
            </Button>
          </Group>
        </form>
      </Modal>
    </Box>
  );
}

function AdminPrompts() {
  const { loading, error, data } = useQuery(GET_PROMPTS);

  if (loading) return <LoadingData />;
  if (error) return <GraphqlError error={error} />;

  const prompts: PromptSummary[] = data?.getPrompts ?? [];

  return (
    <Box p="md">
      <Title order={3} mb="lg">
        Prompt Management
      </Title>
      <Text size="sm" c="dimmed" mb="md">
        Each prompt has one active version (used by the runtime). Editing creates or updates a single draft that you can
        diff and review. Click Set Active when ready to promote.
      </Text>
      {prompts.length === 0 && <Text c="dimmed">No prompts found.</Text>}
      {prompts.map((p) => (
        <PromptRow key={p.id} prompt={p} />
      ))}
    </Box>
  );
}

export default AdminPrompts;
